import * as React from 'react'
import { useNavigate } from 'react-router-dom'

import { AvatarStack, Box, Button, Text } from '@factorialco/deprecated-design-system'
import {
  FeedbacksFeedbackEntryVisibilityEnum,
  FeedbacksFeedbackEntryVisibilityEnumValues,
} from 'generated/resources/datatable.graphql'
import { GivePerformanceFeedbackDocument } from 'generated/resources/graphql'

import { UserSelectorInput } from 'modules/core/components/Inputs/UserSelector'
import Modal from 'modules/core/components/Modal'
import SelectField from 'modules/core/components/SelectField'
import TextArea from 'modules/core/components/TextArea'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import usersWithRegularAccess from 'modules/core/lib/usersWithRegularAccess'
import { isEnumValue } from 'modules/core/lib/utils/enums'
import users from 'modules/core/store/collections/users'
import app from 'modules/core/store/models/app'

import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import { getMemberAvatars } from 'modules/performance_feedbacks/lib/getMemberAvatars'

const MAX_AVATARS_TO_SHOW = 6

const GiveFeedback = () => {
  const navigate = useNavigate()
  const [owners, setOwners] = React.useState<number[]>([])
  const [feedback, setFeedback] = React.useState('')
  const avatarsToShow = getMemberAvatars(owners.slice(0, MAX_AVATARS_TO_SHOW))
  const avatarsLeftover = owners.length - MAX_AVATARS_TO_SHOW
  const [visibility, setVisibility] =
    React.useState<FeedbacksFeedbackEntryVisibilityEnum>('private')

  const {
    access: { id: userId },
  } = useCurrentSession()
  const availableUsers = usersWithRegularAccess(users.active).filter((user) => user.id !== userId)

  const [sendFeedbackMutation, { loading: sendingFeedback }] = useMutation(
    GivePerformanceFeedbackDocument,
    {
      extractorFn: (data) => data.feedbacksMutations.createFeedbacksFeedbackEntry,
    }
  )

  const sendFeedback = async () => {
    const response = await sendFeedbackMutation({
      variables: {
        feedback: feedback,
        receiverIds: owners,
        visibility: visibility,
      },
    })

    const result = handleResult(response, () => null)
    if (result.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('performance_process.overview.participation.send_reminders.success'),
      })
      track(trackingEvents.performanceFeedbacks.sendFeedback)
      navigate(-1)
    }
    return result
  }

  return (
    <ResponsivePage.NestedBody>
      <Modal
        title={i18n.t('performance_feedbacks.pages.give_feedback.title')}
        description={i18n.t('performance_feedbacks.pages.give_feedback.description')}
        illustration={'pencil'}
        onClose={() => navigate(-1)}
        footer={
          <>
            <Button type="secondary" onClick={() => navigate(-1)} disabled={sendingFeedback}>
              {i18n.t('common.cancel')}
            </Button>
            <Button
              onClick={() => sendFeedback()}
              disabled={sendingFeedback || owners.length === 0 || feedback.trim() === ''}
            >
              {i18n.t('performance_feedbacks.pages.give_feedback.send')}
            </Button>
          </>
        }
      >
        <Box gap="s32">
          <Box gap="s16">
            <Text size="300" weight="semibold">
              {i18n.t('performance_feedbacks.pages.give_feedback.who_to_give')}
            </Text>
            {avatarsToShow.length > 1 && (
              <AvatarStack
                size="s32"
                textColor="viridian1000"
                textSize="100"
                backgroundColor="viridian200"
                avatars={avatarsToShow}
                leftover={avatarsLeftover}
                gap="s8"
              />
            )}

            <UserSelectorInput
              label=""
              submitEmptyLabel=""
              selectedIds={owners}
              users={availableUsers}
              placeHolder={i18n.t('common.select')}
              onSubmit={(newSelectedIds) => setOwners(newSelectedIds)}
              allowsEmpty={false}
              outsideCancel
            />
          </Box>
          <Box gap="s16">
            <Text size="300" weight="semibold">
              {i18n.t('performance_feedbacks.pages.give_feedback.who_should_see')}
            </Text>
            <SelectField
              label={''}
              value={visibility}
              none={i18n.t('performance_feedbacks.pages.give_feedback.labels.choose_visibility')}
              options={[
                {
                  label: i18n.t('performance_feedbacks.pages.give_feedback.labels.receiver_only'),
                  value: 'private',
                },
                {
                  label: i18n.t(
                    'performance_feedbacks.pages.give_feedback.labels.receiver_and_manager'
                  ),
                  value: 'private_and_manager',
                },
                {
                  label: i18n.t('performance_feedbacks.pages.give_feedback.labels.manager_only'),
                  value: 'manager_only',
                },
              ]}
              onChange={(value) =>
                isEnumValue(FeedbacksFeedbackEntryVisibilityEnumValues, value) &&
                setVisibility(value)
              }
            />
          </Box>
          <Box gap="s16">
            <Text size="300" weight="semibold">
              {i18n.t('performance_feedbacks.pages.give_feedback.write_feedback')}
            </Text>
            <TextArea
              mandatory
              placeholder={i18n.t('performance_feedbacks.pages.give_feedback.placeholder')}
              value={feedback}
              onChange={(value) => setFeedback(value)}
            />
          </Box>
        </Box>
      </Modal>
    </ResponsivePage.NestedBody>
  )
}

export default GiveFeedback
